import React, { useState, useEffect, useRef, useMemo } from "react";
import { MenuButton } from "../../../components/button";
import { ContainerWithSidebar } from "../../../components/layout/index";
import { Button } from "../../../components/button";
import { FaTrashAlt, FaChevronDown } from "react-icons/fa";
import { MdClear } from "react-icons/md";
import { confirmAlert } from "react-confirm-alert";
import axios from "axios";
import { useTranslation } from "react-i18next";
import SimpleModal from "components/simple-modal";
import { toast } from "react-toastify";
import AddItemToOrder from "./AddItemToOrder";
import LineBranchSelector from "./LineBranchSelector";
import { isStaleReading } from "utils/heritageStaleReading";
import { vendorStockLabel } from "./vendorStockLabel";
import styled from "styled-components";
import { theme } from "styles/theme";
import Tab from "components/tab";
import { groupItemsBySelectedVendor } from "./groupItemsBySelectedVendor";

import "./index.scss";

/** Set true to show Refresh Heritage Prices on the Review tab. */

// Custom container that accounts for sticky tab bar
const PurchasingContainerWithSidebar = styled(ContainerWithSidebar)`
  > div:first-child {
    top: calc(${theme.height.navigation} + 45px); // navigation + tab bar height
    height: calc(100vh - ${theme.height.navigation} - 45px - 20px); // subtract extra buffer for margins/padding
  }
`;

const Review = (props) => {
  const { t } = useTranslation();
  const tBase = "views.purchasing.review.index";
  const tr = (key) => t(`${tBase}.${key}`);

  const [orders, setOrders] = useState([]);
  const [details, setDetails] = useState({});
  const [vendors, setVendors] = useState([]);
  const [modalVendor, setModalVendor] = useState(null);
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
  /** The company's favorite ("my") branches — the options a line can be ordered from (#37). */
  const [favoriteBranches, setFavoriteBranches] = useState([]);
  /** Live per-branch stock for the order's Heritage parts, keyed by part number. */
  const [branchStockByPart, setBranchStockByPart] = useState({});

  const fetchReview = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/fetchReview`
    );

    setOrders(response.data);
  };

  const fetchReviewDetails = async (id) => {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/fetchReview/${id}`
    );

    setDetails(response.data.review);
    setVendors(response.data.vendors);
    return response.data;
  };

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/local-branches`)
      .then((res) => {
        if (cancelled) return;
        setFavoriteBranches((res.data?.branches || []).filter((b) => b.is_favorite));
      })
      .catch(() => {
        // No Heritage connection (or no favorites) simply means no branch
        // options — the selector renders with "Vendor default" alone.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Live stock per favorite branch for the order's Heritage parts, so the
  // branch options can say what choosing them buys. Async on purpose:
  // getInventory answers in seconds and must never hold up the review page.
  useEffect(() => {
    const parts = [
      ...new Set(
        (details.items || [])
          .map((item) =>
            item.vendors?.find(
              (v) => String(v.vendor_id) === String(item.vendor) && v.is_heritage
            )?.vendor_part_number
          )
          .filter(Boolean)
      ),
    ].slice(0, 25);
    setBranchStockByPart({});
    if (parts.length === 0) return undefined;

    let cancelled = false;
    axios
      .post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/items/branch-stock`, {
        partNumbers: parts,
      })
      .then((res) => {
        if (cancelled) return;
        const byPart = {};
        for (const row of res.data?.parts || []) {
          byPart[row.heritage_part_number] = row.stock || [];
        }
        setBranchStockByPart(byPart);
      })
      .catch(() => {
        // The stock hints are an enhancement; the selector works without them.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details.items]);

  const removeCartItem = async (id) => {
    confirmAlert({
      title: tr("Delete Item"),
      message: tr("Are you sure you want to remove this item?"),
      buttons: [
        {
          label: "No",
          onClick: () => { },
        },
        {
          label: tr("Yes"),
          onClick: async () => {
            await axios.delete(
              `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/removeCartItem`,
              {
                data: {
                  cartItemId: id,
                },
              }
            );

            fetchReviewDetails(details.id);
          },
        },
      ],
    });
  };

  const deleteOrder = async (orderId) => {
    confirmAlert({
      title: tr("Delete Order"),
      message: tr("Are you sure you want to delete this order?"),
      buttons: [
        {
          label: "No",
          onClick: () => { },
        },
        {
          label: tr("Yes"),
          onClick: async () => {
            await axios.delete(
              `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/deleteOrder/${orderId}`
            );

            props.fetchCounts();
            fetchReview();
            setDetails({});
          },
        },
      ],
    });
  };

  const finalize = async (orderId) => {
    confirmAlert({
      title: tr("Finalize Order"),
      message: tr("Are you sure you want to finalize this order?"),
      buttons: [
        {
          label: "No",
          onClick: () => { },
        },
        {
          label: tr("Yes"),
          onClick: async () => {
            await axios.put(
              `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/finalize`,
              {
                orderId,
              }
            );

            props.fetchCounts();
            fetchReview();
            setDetails({});
          },
        },
      ],
    });
  };

  const [isRefreshingPrices, setIsRefreshingPrices] = useState(false);

  // #7 — live price + stock for THIS order's Heritage lines, persisted via the
  // same machinery as the vendor page's "Update pricing & stock". Runs
  // automatically when an order is opened (silent) and on the button (loud).
  const refreshPrices = async (orderId, { silent = false } = {}) => {
    setIsRefreshingPrices(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/refresh-order-stock-price`,
        { orderId }
      );

      const { updated, errors } = response.data;
      if (!silent) {
        if (updated?.length > 0) {
          toast.success(
            tr("Refreshed vendor items").replace("{{count}}", String(updated.length))
          );
        } else if (!errors?.length) {
          // vendors > 0 means the order HAS heritage lines but Heritage
          // returned no data for them (e.g. parts unknown upstream) — say
          // that, not "no items found".
          toast.info(
            response.data.vendors > 0
              ? tr("No live data returned")
              : tr("No Heritage-linked items found to refresh")
          );
        }
      }
      // A vendor that failed (no branch picked, credentials) is worth knowing
      // about even on the silent path — stale numbers would mislead quietly.
      (errors || []).forEach((e) => toast.error(e.message));
      
      fetchReviewDetails(orderId);
    } catch (error) {
      console.error("Error refreshing prices:", error);
      toast.error(error.response?.data?.message || "Failed to refresh prices");
    } finally {
      setIsRefreshingPrices(false);
    }
  };

  useEffect(() => {
    fetchReview();
  }, []);

  const [activeVendorTab, setActiveVendorTab] = useState("");

  const unassignedLabel = tr("Unassigned");
  const vendorGroups = useMemo(
    () =>
      details.items?.length
        ? groupItemsBySelectedVendor(details.items, vendors, unassignedLabel)
        : [],
    [details.items, vendors, unassignedLabel]
  );

  const tabData = useMemo(() => {
    if (!details.items || !details.items.length) return null;

    const data = {};

    vendorGroups.forEach((group) => {
      data[group.key] = {
        tab: group.vendorName,
        count: group.items.length,
      };
    });

    return data;
  }, [details.items, vendorGroups]);

  useEffect(() => {
    if (vendorGroups.length > 0) {
      const exists = vendorGroups.some((g) => g.key === activeVendorTab);
      if (!exists) {
        setActiveVendorTab(vendorGroups[0].key);
      }
    }
  }, [vendorGroups, activeVendorTab]);

  const handleVendorChanged = async (newVendorValue) => {
    const data = await fetchReviewDetails(details.id);
    if (newVendorValue) {
      const rawStr = String(newVendorValue).trim();
      const allVendors = data?.vendors || vendors;
      const matched = allVendors?.find(
        (v) =>
          String(v.vendor_id) === rawStr ||
          v.vendor_name?.toLowerCase() === rawStr.toLowerCase()
      );
      if (matched) {
        setActiveVendorTab(`vendor_${matched.vendor_id}`);
      } else {
        setActiveVendorTab(`other_${rawStr.toLowerCase().replace(/\s+/g, "_")}`);
      }
    } else {
      setActiveVendorTab("unassigned");
    }
  };

  const renderReviewItem = (item, index) => (
    <div key={item.id || index} className="finalize-item">
      <div className="part-details">
        <div className="review-part-name">
          <div>
            {item.pc_name} - {item.category_name}
          </div>
          <div className="review-part">{item.item_name}</div>
        </div>

        <img
          className="review-img"
          src={item.imageUri}
          alt={item.item_name}
        />
      </div>

      <div className="detail-row">
        <div className="review-detail-group">
          <div className="review-detail-text">{tr("Minimum")}</div>
          <div className="review-detail-text">{item.minimum}</div>
        </div>

        <div className="review-detail-group">
          <div className="review-detail-text">{tr("Maximum")}</div>
          <div className="review-detail-text">{item.maximum}</div>
        </div>

        <div className="review-detail-group">
          <div className="review-detail-text">{tr("Stock")}</div>
          <div className="review-detail-text">{item.stock}</div>
        </div>
      </div>

      <div className="detail-row">
        <div className="review-detail-group">
          <div className="review-detail-text">{tr("Outstanding")}</div>
          <div className="review-detail-text">{item.outstanding || 0}</div>
        </div>

        <div className="review-detail-group">
          <div className="review-detail-text">{tr("Recommended")}</div>
          <div className="review-detail-text">
            {item.maximum - item.stock - item.outstanding > 0
              ? item.maximum - item.stock - item.outstanding
              : 0}
          </div>
        </div>

        <div className="review-detail-group">
          <div className="review-detail-text">{tr("Ordered")}</div>
          <div className="review-detail-text">{item.quantity}</div>
        </div>
      </div>

      <div className="detail-row">
        <div className="review-detail-group-last">
          <div className="review-detail-text">{tr("Update Order")}</div>
          <QuantityInput
            className="review-qty-input"
            item={item}
            fetchReviewDetails={fetchReviewDetails}
            details={details}
          />
        </div>

        <div className="review-detail-group-last">
          <div className="review-detail-text">{tr("Remove Item")}</div>
          <div className="review-trash-container">
            <FaTrashAlt
              onClick={() => removeCartItem(item.id)}
              size={20}
              color="red"
              className="review-trash"
            />
          </div>
        </div>
      </div>

      <VendorContainer
        vendors={vendors}
        details={details}
        item={item}
        fetchReviewDetails={fetchReviewDetails}
        onVendorChanged={handleVendorChanged}
        openVendorModal={(vendor) => {
          setModalVendor(vendor);
          setIsVendorModalOpen(true);
        }}
      />

      {(() => {
        /*
          #37 — only a line whose CHOSEN vendor is Heritage-linked can pick a
          branch; everything else orders exactly as before. One line = one
          branch; differing branches split the send into one Heritage order
          per branch.
        */
        const selectedVendor = item.vendors?.find(
          (v) => String(v.vendor_id) === String(item.vendor)
        );
        if (!selectedVendor?.is_heritage || !selectedVendor?.vendor_part_number) {
          return null;
        }
        return (
          <LineBranchSelector
            item={item}
            favoriteBranches={favoriteBranches}
            branchStockByPart={branchStockByPart}
            partNumber={selectedVendor.vendor_part_number}
            onChanged={() => fetchReviewDetails(details.id)}
          />
        );
      })()}
    </div>
  );

  const activeGroup = vendorGroups.find((g) => g.key === activeVendorTab) || vendorGroups[0];

  return (
    <>
      <PurchasingContainerWithSidebar>
        <div className="purchasing-sidebar" style={{ backgroundColor: "#edf1f4" }}>
          <div className="review-menu-title">{tr("Pending Orders")}</div>
          <hr />
          {orders &&
            orders?.map((order) => {
              return (
                <MenuButton
                  key={order.id}
                  style={{
                    textAlign: "left",
                    marginTop: 3,
                    marginBottom: 3,
                    fontSize: "90%",
                  }}
                  onClick={async () => {
                    await fetchReviewDetails(order.id);
                    // #7 — fresh stock/prices at PO time, then re-read
                    refreshPrices(order.id, { silent: true });
                  }}
                >
                  <span>#{order.id}</span>
                  <span>{new Date(order.timestamp).toLocaleDateString()}</span>
                  <span>({order.name})</span>
                </MenuButton>
              );
            })}
        </div>
        <div>
          {details && details.hasOwnProperty("id") ? (
            <div>
              <div className="finalize-header">
                <div className="finalize-header-meta">
                  <div className="finalize-header-title">Order: #{details.id}</div>
                  <div className="finalize-header-detail">
                    {tr("Created")}:{" "}
                    {new Date(details.timestamp).toLocaleDateString()}
                  </div>
                  <div className="finalize-header-detail">
                    {tr("Entered By")}: {details.name}
                  </div>
                </div>

                <div className="finalize-header-actions">
                  <Button
                    size="small"
                    onClick={() => {
                      deleteOrder(details.id);
                    }}
                  >
                    {tr("Delete")}
                  </Button>

                  <Button
                    size="small"
                    onClick={() => {
                      refreshPrices(details.id);
                    }}
                    disabled={isRefreshingPrices}
                  >
                    {isRefreshingPrices
                      ? tr("Refreshing...")
                      : tr("Refresh vendor stock & prices")}
                  </Button>

                  <Button size="small" onClick={() => finalize(details.id)} primary>
                    {tr("Finalize")}
                  </Button>
                </div>
              </div>

              {/* #34 — add items without leaving the order */}
              <AddItemToOrder
                orderId={details.id}
                onAdded={() => fetchReviewDetails(details.id)}
              />

              {tabData && (
                <div className="review-vendor-tabs-wrapper">
                  <Tab
                    headerOnly={true}
                    defaultTab={activeVendorTab}
                    onClick={(key) => setActiveVendorTab(key)}
                    data={tabData}
                  />
                </div>
              )}

              <div className="review-vendor-groups">
                {activeGroup && (
                  <div
                    key={activeGroup.key}
                    className="review-vendor-group"
                  >
                    <div className="items">
                      {activeGroup.items.map((item, index) =>
                        renderReviewItem(item, index)
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </PurchasingContainerWithSidebar>

       <SimpleModal
          isOpen={isVendorModalOpen}
          onClose={() => setIsVendorModalOpen(false)}
        >
          {modalVendor && (
            <div>
              <h3 className="text-lg font-bold mb-2">
                {modalVendor.vendor_name || modalVendor.name || tr("Vendor Details")}
              </h3>
              <p>
                <strong>Price:</strong>{" "}
                {modalVendor.price != null && !isNaN(Number(modalVendor.price))
                  ? (Number(modalVendor.price) / 100).toLocaleString("en-us", {
                      style: "currency",
                      currency: "USD",
                    })
                  : "N/A"}
              </p>
              {vendorStockLabel(modalVendor, tr("branch unknown")) && (
                <p>
                  <strong>{tr("Vendor stock")}:</strong>{" "}
                  {vendorStockLabel(modalVendor, tr("branch unknown"))}
                </p>
              )}
              <p>
                <strong>Latest Status:</strong> {modalVendor.latest_status || "N/A"}
              </p>
              <p>
                <strong>Price Updated:</strong>{" "}
                {modalVendor.last_update
                  ? new Date(modalVendor.last_update).toLocaleDateString()
                  : "N/A"}
                {isStaleReading(modalVendor.last_update) && (
                  <span className="review-stale-reading"> ({tr("may be out of date")})</span>
                )}
              </p>
            </div>
          )}
      </SimpleModal>
    </>
  );
};

const QuantityInput = (props) => {
  const [quantity, setQuantity] = useState(props.item.quantity);

  const updateQuantity = async (cartItemId, itemId, quantity) => {
    if (quantity && quantity >= 0) {
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/updateQuantity`,
        {
          cartItemId,
          itemId,
          quantity,
        }
      );

      if (response.status === 200) {
        setQuantity(quantity);
        props.fetchReviewDetails(props.details.id);
      }
    }
  };

  return (
    <input
      className="review-input"
      value={quantity}
      onChange={(e) => setQuantity(e.target.value)}
      onBlur={(e) =>
        updateQuantity(props.item.id, props.item.item_id, e.target.value)
      }
    />
  );
};

const VendorContainer = (props) => {
  const { t } = useTranslation();
  const tBase = "views.purchasing.review.index";
  const tr = (key) => t(`${tBase}.${key}`);

  const preferredVendorId = props.item.preferred_vendor_id;
  const autoAssignedRef = useRef(false);

  const currentValue =
    props.item.vendor != null && props.item.vendor !== ""
      ? String(props.item.vendor)
      : "";

  const [selectedValue, setSelectedValue] = useState(currentValue);
  const [isOpen, setIsOpen] = useState(false);
  const [other, setOther] = useState("");

  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  // silent: skip the active-tab switch (used by auto-assign so the initial
  // load doesn't park the user on the last auto-assigned vendor's tab).
  const updateVendor = async (vendor, { silent = false } = {}) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/updateVendor`,
        { orderId: props.details.id, itemId: props.item.item_id, vendor }
      );

      if (response.status === 200) {
        setSelectedValue(String(vendor));
        if (!silent && props.onVendorChanged) {
          props.onVendorChanged(vendor);
        } else if (props.fetchReviewDetails) {
          props.fetchReviewDetails(props.details.id);
        }
      }
    } catch (error) {
      console.error("Error updating vendor:", error);
      toast.error(error.response?.data?.message || tr("Failed to update vendor"));
    }
  };

  useEffect(() => {
    autoAssignedRef.current = false;
  }, [props.item.item_id]);

  useEffect(() => {
    setSelectedValue(currentValue);
  }, [currentValue, props.item.item_id]);

  useEffect(() => {
    const isUnset = props.item.vendor == null;
    if (isUnset && preferredVendorId != null && preferredVendorId !== "") {
      if (autoAssignedRef.current) return;
      autoAssignedRef.current = true;
      updateVendor(preferredVendorId, { silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.item.item_id, preferredVendorId, props.item.vendor]);

  const availableVendors = useMemo(() => {
    const map = new Map();

    if (props.item.vendors && props.item.vendors.length > 0) {
      props.item.vendors.forEach((v) => {
        const id = String(v.vendor_id);
        map.set(id, {
          id,
          name: v.vendor_name,
          price: v.price,
          rawVendorObj: v,
          isPreferred:
            preferredVendorId != null && String(preferredVendorId) === id,
        });
      });
    }

    if (props.vendors && props.vendors.length > 0) {
      props.vendors.forEach((v) => {
        const id = String(v.vendor_id);
        if (!map.has(id)) {
          map.set(id, {
            id,
            name: v.vendor_name,
            price: null,
            rawVendorObj: v,
            isPreferred:
              preferredVendorId != null && String(preferredVendorId) === id,
          });
        }
      });
    }

    return Array.from(map.values());
  }, [props.item.vendors, props.vendors, preferredVendorId]);

  const selectedVendorObj = useMemo(() => {
    if (!selectedValue || String(selectedValue).trim() === "") return null;
    return (
      availableVendors.find(
        (v) => v.id === String(selectedValue) || v.name === selectedValue
      ) || null
    );
  }, [availableVendors, selectedValue]);

  // A vendor typed by hand isn't in availableVendors but must still show in
  // the box (and be clearable) rather than falling back to "Select Vendor".
  const displayName =
    selectedVendorObj?.name ||
    (selectedValue && String(selectedValue).trim() !== ""
      ? String(selectedValue)
      : null);

  const handleMainClick = () => {
    if (!selectedVendorObj) {
      setIsOpen(!isOpen);
      return;
    }
    const vendorObj = selectedVendorObj?.rawVendorObj || {
      vendor_name: selectedVendorObj?.name || selectedValue || tr("Vendor"),
      price: selectedVendorObj?.price ?? null,
      latest_status: "N/A",
      last_update: null,
    };
    if (props.openVendorModal) {
      props.openVendorModal(vendorObj);
    }
  };

  // The visible selection is only updated by updateVendor's success path —
  // setting it here before the server confirms would show a vendor the order
  // does not actually have if the request fails.
  const handleSelectVendor = (vendorId) => {
    updateVendor(vendorId);
    setIsOpen(false);
  };

  const handleClearVendor = (e) => {
    e.stopPropagation();
    autoAssignedRef.current = true;
    updateVendor("");
  };

  return (
    <div className="vendor-dropdown-container" ref={containerRef}>
      <div className="vendor-dropdown-label">{tr("Choose Vendor")}:</div>

      <div className="vendor-box-wrapper">
        <div className="vendor-box">
          <div
            className="vendor-box-main"
            onClick={handleMainClick}
            title={
              selectedVendorObj
                ? tr("Click to view vendor details and price")
                : tr("Click to choose vendor")
            }
          >
            <span className="vendor-box-name">
              {displayName || tr("Select Vendor")}
            </span>
            {selectedVendorObj?.isPreferred && (
              <span className="preferred-vendor-badge">
                {" "}
                ({tr("Preferred")})
              </span>
            )}
          </div>

          {displayName && (
            <button
              type="button"
              className="vendor-box-clear"
              onClick={handleClearVendor}
              aria-label={tr("Clear vendor")}
              title={tr("Clear vendor")}
            >
              <MdClear size={16} />
            </button>
          )}

          <button
            type="button"
            className={`vendor-box-arrow ${isOpen ? "open" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            aria-label={tr("Toggle vendor dropdown")}
          >
            <FaChevronDown size={14} />
          </button>
        </div>

        {isOpen && (
          <div className="vendor-dropdown-menu">
            {availableVendors.length > 0 ? (
              availableVendors.map((v) => (
                <div
                  key={v.id}
                  className={`vendor-dropdown-item ${
                    v.id === String(selectedValue) ? "selected" : ""
                  }`}
                  onClick={() => handleSelectVendor(v.id)}
                >
                  <div className="vendor-item-info">
                    <span className="vendor-item-name">{v.name}</span>
                    {v.isPreferred && (
                      <span className="preferred-vendor-badge">
                        {" "}
                        ({tr("Preferred")})
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="vendor-dropdown-item empty">
                <span>{tr("No vendors available")}</span>
              </div>
            )}
            {/* A vendor that isn't in the list can still be assigned by name —
                the send flow emails those as their own PO. */}
            <div className="vendor-dropdown-other">
              <input
                className="vendor-dropdown-other-input"
                placeholder={tr("Other Vendor")}
                value={other}
                onChange={(e) => setOther(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && other.trim() !== "") {
                    updateVendor(other.trim());
                    setOther("");
                    setIsOpen(false);
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Review;
